import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Row, Select, Tooltip, message } from 'antd';
import { CopyOutlined, EyeInvisibleOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { RECURSO_EMPLEADOS_GESTIONAR, leerClaveInformesEmpleado, leerEstadoEmpleado } from '../../api/empleadosApi';
import type { EstadoEmpleadoCambio } from '../../api/empleadosApi';
import { usePermissions } from '../../contexts/PermissionContext';
import { Seccion } from './CamposEmpleado';
import { errorEmpleado } from './errores';

export const AVISO_REACTIVAR = 'Al activarlo se reabren su delegación y su zona, y vuelven a estar en vigor sus roles, su visibilidad de asesores y sus niveles. Las subregiones no se reabren.';
export const AVISO_BAJA = 'Está de baja: solo se puede cambiar su estado.';
export const PREFIJO_DATOS_GUARDADOS = 'Los datos se han guardado; el estado no:';
export const PREFIJO_ESTADO_GUARDADO = 'El estado se ha guardado; los datos no:';
/** Ficha activa al reactivar a un empleado de baja. Pendiente de confirmar con el responsable: se cambia aquí. */
export const FICHA_ACTIVA_AL_REACTIVAR = true;
export const AYUDA_CLAVE = 'Clave con la que se identifica al empleado en los informes';
export const VER_CLAVE = 'Ver clave de informes';
export const OCULTAR_CLAVE = 'Ocultar clave de informes';
export const CLAVE_ENMASCARADA = '••••••••••••';
export const CLAVE_COPIADA = 'Clave copiada';
export const ERROR_COPIAR_CLAVE = 'No se ha podido copiar. Selecciónala y cópiala a mano.';
/** Segundos que la clave se queda a la vista antes de volver a ocultarse sola. */
export const SEGUNDOS_CLAVE_VISIBLE = 30;

/** El texto del servidor, sin etiquetas HTML. */
const sinHtml = (s: string): string => s.replace(/<[^>]*>/g, '').trim();
export const ERROR_LEER_ESTADO = 'No se ha podido leer el estado del empleado. Cierra y vuelve a abrir la edición.';

/** Lo que el formulario edita del estado. `fechaBaja` va en yyyy-MM-dd y solo tiene valor con Activo en No. */
export interface EstadoForm {
  activo: boolean;
  fichaActiva: boolean;
  fechaBaja: string | null;
}

const FORMATO = 'YYYY-MM-DD';
const hoy = () => dayjs().format(FORMATO);
const SI_NO = [{ value: true, label: 'Sí' }, { value: false, label: 'No' }];

/** Activo en No fuerza Ficha activa en No (la combinación contraria no existe) y propone la fecha de baja: la que ya había o hoy. */
export const conActivo = (actual: EstadoForm, activo: boolean, inicial: EstadoForm): EstadoForm =>
  activo
    ? { activo: true, fichaActiva: inicial.activo ? inicial.fichaActiva : FICHA_ACTIVA_AL_REACTIVAR, fechaBaja: null }
    : { activo: false, fichaActiva: false, fechaBaja: inicial.fechaBaja ?? hoy() };

/** La fecha de baja no puede ser futura. */
export const esFechaFutura = (d: Dayjs): boolean => d.isAfter(dayjs(), 'day');

export const estadoCambiado = (inicial: EstadoForm, actual: EstadoForm): boolean =>
  inicial.activo !== actual.activo || inicial.fichaActiva !== actual.fichaActiva || (!actual.activo && inicial.fechaBaja !== actual.fechaBaja);

export const aCuerpoEstado = (e: EstadoForm): EstadoEmpleadoCambio =>
  e.activo ? { activo: true, fichaActiva: e.fichaActiva } : { activo: false, fichaActiva: false, fechaBaja: e.fechaBaja ?? hoy() };

/** Lee el estado al abrir la edición (solo con Empleados.Gestionar) y lleva el valor que se está editando. */
export const useEstadoEmpleado = (idEmpleado: number | undefined, habilitado: boolean) => {
  const [inicial, setInicial] = useState<EstadoForm | null>(null);
  const [valor, setValor] = useState<EstadoForm | null>(null);
  const [tieneClave, setTieneClave] = useState(false);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!habilitado || idEmpleado === undefined) return undefined;
    let vigente = true;
    leerEstadoEmpleado(idEmpleado)
      .then((e) => {
        if (!vigente) return;
        const leido: EstadoForm = { activo: e.activo, fichaActiva: e.fichaActiva, fechaBaja: e.activo ? null : (e.fechaBaja?.slice(0, 10) ?? hoy()) };
        setInicial(leido);
        setValor(leido);
        setTieneClave(e.tieneClaveInformes);
      })
      .catch(() => { if (vigente) setFallo(true); });
    return () => { vigente = false; };
  }, [idEmpleado, habilitado]);

  const cambio = inicial && valor && estadoCambiado(inicial, valor) ? aCuerpoEstado(valor) : null;
  /** Lo editado pasa a ser lo guardado: un reintento no vuelve a enviarlo. */
  const marcarGuardado = () => setInicial(valor);
  return { inicial, valor, setValor, tieneClave, fallo, cambio, marcarGuardado };
};

type Estado = ReturnType<typeof useEstadoEmpleado>;

/**
 * Tarjeta «Estado» de la edición. Sin Empleados.Gestionar no pinta nada. La clave de informes va enmascarada: el valor se pide solo al pulsar el ojo,
 * vive únicamente en el estado de este componente y se descarta al ocultarlo (a los 30 s, al pulsar de nuevo, al cambiar de empleado o al cerrar).
 */
export const EstadoEmpleado: React.FC<{ estado: Estado; idEmpleado?: number }> = ({ estado, idEmpleado }) => {
  const { hasPermission } = usePermissions();
  const { inicial, valor, setValor, tieneClave, fallo } = estado;
  const [clave, setClave] = useState<string | null>(null);
  const [pidiendo, setPidiendo] = useState(false);
  const puede = hasPermission(RECURSO_EMPLEADOS_GESTIONAR);

  // Cambiar de empleado descarta lo que hubiera a la vista.
  useEffect(() => { setClave(null); }, [idEmpleado]);
  // A los 30 s se oculta sola; la limpieza también cubre cerrar el formulario.
  useEffect(() => {
    if (clave === null) return undefined;
    const t = setTimeout(() => setClave(null), SEGUNDOS_CLAVE_VISIBLE * 1000);
    return () => clearTimeout(t);
  }, [clave]);
  if (!puede) return null;

  const alternarClave = async () => {
    if (clave !== null) { setClave(null); return; }
    if (idEmpleado === undefined || pidiendo) return;
    setPidiendo(true);
    try {
      const leida = await leerClaveInformesEmpleado(idEmpleado);
      setClave(leida);
      if (leida === null) message.warning('El empleado no tiene clave de informes.');
    } catch (e) {
      message.error(sinHtml(errorEmpleado(e).mensaje));
    } finally {
      setPidiendo(false);
    }
  };

  const copiar = async () => {
    if (clave === null) return;
    try {
      await navigator.clipboard.writeText(clave);
      message.success(CLAVE_COPIADA);
    } catch {
      message.error(ERROR_COPIAR_CLAVE);
    }
  };

  const listo = !!inicial && !!valor;
  const reactivar = !!inicial && !inicial.activo && !!valor?.activo;

  return (
    <Seccion titulo="Estado">
      {fallo ? (
        <p role="alert" style={{ margin: '0 0 8px' }}>{ERROR_LEER_ESTADO}</p>
      ) : (
        <Row gutter={[12, 0]}>
          <Col xs={24} md={5}>
            <Form.Item label="Activo" htmlFor="empleado-estado-activo">
              <Select id="empleado-estado-activo" disabled={!listo} options={SI_NO} value={valor?.activo}
                onChange={(a: boolean) => inicial && valor && setValor(conActivo(valor, a, inicial))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={5}>
            <Form.Item label="Ficha activa" htmlFor="empleado-estado-ficha">
              <Select id="empleado-estado-ficha" disabled={!listo || !valor?.activo} options={SI_NO} value={valor?.fichaActiva}
                onChange={(f: boolean) => valor && setValor({ ...valor, fichaActiva: f })} />
            </Form.Item>
          </Col>
          {valor && !valor.activo && (
            <Col xs={24} md={6}>
              <Form.Item label="Fecha de baja" htmlFor="empleado-estado-fecha-baja">
                <DatePicker id="empleado-estado-fecha-baja" format="DD/MM/YYYY" allowClear={false} style={{ width: '100%' }}
                  value={valor.fechaBaja ? dayjs(valor.fechaBaja) : null}
                  disabledDate={esFechaFutura}
                  onChange={(d: Dayjs | null) => setValor({ ...valor, fechaBaja: d ? d.format(FORMATO) : hoy() })} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={8}>
            <Form.Item label="Clave de informes">
              <div className="empleado-clave">
                {!listo ? <span>Leyendo…</span> : !tieneClave ? <span>Sin clave</span> : clave !== null ? (
                  <code className="empleado-clave__valor fct-mono">{clave}</code>
                ) : (
                  <span>{CLAVE_ENMASCARADA}</span>
                )}
                {listo && tieneClave && (
                  <Button size="small" type="text" loading={pidiendo} aria-label={clave !== null ? OCULTAR_CLAVE : VER_CLAVE}
                    icon={clave !== null ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={alternarClave} />
                )}
                {clave !== null && (
                  <Button size="small" type="text" icon={<CopyOutlined />} onClick={copiar}>Copiar</Button>
                )}
                <Tooltip title={AYUDA_CLAVE}>
                  <InfoCircleOutlined className="empleado-info" tabIndex={0} role="img" aria-label="Ayuda" data-ayuda="claveInformes" />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
        </Row>
      )}
      {reactivar && <p className="empleado-estado__nota">{AVISO_REACTIVAR}</p>}
    </Seccion>
  );
};
